import { useEffect, useRef, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import usePackageVulnerabilities from '../../../../hooks/api/packages/usePackageVulnerabilities';
import useSelectedVuln from '../../../../hooks/api/packages/useSelectedVuln';
import useSortedVulnerabilities from '../../hooks/useSortedVulnerabilities';
import type { SortableColumn, SortType } from '../types';

import VulnerabilityDrawer from './Drawer';
import VulnerabilitiesTable from './Table';

const DRAWER_ANIMATION_TIMEOUT = 500;

type VulnerabilitiesTableContainerProps = {
  params: {
    packageLocator: string;
    version: string;
  };
};

const VulnerabilitiesTableContainer: VoidFunctionComponent<VulnerabilitiesTableContainerProps> = ({ params }) => {
  //  Note that we can't gate this with a isFeatureEnabled check as it violates the Rules of Hooks™️
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [sortByColumn, setSortByColumn] = useState<SortableColumn>('cvss');
  const [sortType, setSortType] = useState<SortType>('desc');
  const [selectedVulnId, setSelectedVulnId] = useState<string | null>(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
  const { data: vulnerabilities, isError, isLoading } = usePackageVulnerabilities(
    params.packageLocator,
    params.version,
  );
  const sortedVulnerabilities = useSortedVulnerabilities(vulnerabilities, sortByColumn, sortType);
  const selectedVulnQuery = useSelectedVuln(selectedVulnId, selectedRevisionId);

  //  This is used to ignore clicks on the table body when the Drawer is open, so that it doesn't close when we click "outside" the Drawer onto the table
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const locator = `${params.packageLocator}$${params.version}`;
  const onOpenDrawer = (vulnId: string) => {
    clearCloseTimeout();
    if (!isDrawerOpen) {
      setIsDrawerOpen(true);
    }
    setSelectedVulnId(vulnId);
    setSelectedRevisionId(locator);
  };

  const onCloseDrawer = () => {
    clearCloseTimeout();
    setIsDrawerOpen(false);
    //  Keep the content mounted until the close animation is complete.
    closeTimeoutRef.current = setTimeout(() => {
      setSelectedVulnId(null);
      setSelectedRevisionId(null);
      closeTimeoutRef.current = null;
    }, DRAWER_ANIMATION_TIMEOUT + 10);
  };

  return (
    <div className="container padding-top-lg" data-test-id="package-inventory-package-version">
      <VulnerabilityDrawer
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        version={params.version}
        packageRevisionLocator={locator}
        tableBodyRef={tableBodyRef}
        slideDuration={DRAWER_ANIMATION_TIMEOUT}
        selectedVuln={selectedVulnQuery.data}
        error={selectedVulnQuery.error}
        isLoading={selectedVulnQuery.isLoading}
      />
      <VulnerabilitiesTable
        sortByColumn={sortByColumn}
        setSortByColumn={setSortByColumn}
        sortType={sortType}
        setSortType={setSortType}
        vulnerabilities={sortedVulnerabilities}
        isError={isError}
        isLoading={isLoading}
        onVulnSelect={onOpenDrawer}
        tableBodyRef={tableBodyRef}
      />
    </div>
  );
};

export default VulnerabilitiesTableContainer;
